import { FC, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Stock } from "@/models/stock";
import { useAtomValue } from "jotai";
import { ortexColorState } from "@/lib/store";
import { timeSettingsState } from "@/lib/time/state";
import { resolveUserZone, zoneCity, zoneLabel } from "@/lib/time/zone";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import DoDisturbOnOutlinedIcon from "@mui/icons-material/DoDisturbOnOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublicIcon from "@mui/icons-material/Public";
import { Box, Chip, ClickAwayListener, IconButton, Paper, Popper, Tooltip, Typography, useMediaQuery } from "@mui/material";

const StockHeaderSession: FC<{ stock: Stock }> = ({ stock }) => {
    const ortexColor = useAtomValue(ortexColorState);
    const isCompact = useMediaQuery("(max-width: 768px)");
    const [session, setSession] = useState<string | null>("Market Closed");
    const [popperAnchor, setPopperAnchor] = useState<HTMLElement | null>(null);
    const [countdown, setCountdown] = useState<string>("");
    // The globe toggle starts from Settings > Date & time > Indexes and market charts (#1281);
    // "local" is the user's zone there, not necessarily the device's.
    const timeSettings = useAtomValue(timeSettingsState);
    const userZone = resolveUserZone(timeSettings);
    const [useLocalTz, setUseLocalTz] = useState(timeSettings.indexes === "user");
    useEffect(() => setUseLocalTz(timeSettings.indexes === "user"), [timeSettings.indexes]);

    useEffect(() => {
        const calculateSession = () => {
            if (stock.exchange_tz && stock.exchange_th) {
                const currentDay = dayjs().tz(stock.exchange_tz.trimEnd());
                const trading_day = dayjs(stock.trading_day).tz(stock.exchange_tz.trimEnd()).utc().format("DD");
                const trading_day_next = dayjs(stock.trading_day_next).tz(stock.exchange_tz.trimEnd()).utc().format("DD");
                const currentTime: number = parseInt(`${currentDay.get("hour")}${currentDay.format("mm")}`);
                const exchangeTime: number[] = stock.exchange_th
                    ?.split(",")
                    .join("-")
                    .split("-")
                    .map((item) => parseInt(item));
                if (!stock.trading_day && !stock.trading_day_next) setSession(null);
                else if (currentDay.format("DD") === trading_day || currentDay.format("DD") === trading_day_next) {
                    if (exchangeTime.length === 2) {
                        if (currentTime >= exchangeTime[0] && currentTime < exchangeTime[1]) {
                            setSession("Main session");
                        } else {
                            setSession("Market Closed");
                        }
                    } else {
                        if (currentTime >= exchangeTime[0] && currentTime < exchangeTime[1]) {
                            setSession("Premarket");
                        } else if (currentTime >= exchangeTime[2] && currentTime < exchangeTime[3]) {
                            setSession("Main session");
                        } else if (currentTime >= exchangeTime[4] && currentTime < exchangeTime[5]) {
                            setSession("After hours");
                        } else {
                            setSession("Market Closed");
                        }
                    }
                } else {
                    setSession("Market Closed");
                }
            } else {
                setSession(null);
            }
        };

        calculateSession();
        const interval = setInterval(calculateSession, 60_000);
        return () => clearInterval(interval);
    }, [stock]);

    useEffect(() => {
        if (!stock.exchange_tz || !stock.exchange_th) {
            setCountdown("");
            return;
        }

        const hhmm2min = (t: number) => {
            const s = String(t).padStart(4, "0");
            return parseInt(s.slice(0, 2)) * 60 + parseInt(s.slice(2));
        };

        const allTimes = stock.exchange_th.split(",").join("-").split("-").map(Number).map(hhmm2min);

        const tick = () => {
            const now = dayjs().tz(stock.exchange_tz.trimEnd());
            const nowMin = now.hour() * 60 + now.minute();
            const nowSec = now.second();

            let targetMin: number | null = null;

            if (session === "Premarket" && allTimes.length >= 2) {
                targetMin = allTimes[1];
            } else if (session === "Main session") {
                targetMin = allTimes.length === 2 ? allTimes[1] : (allTimes[3] ?? null);
            } else if (session === "After hours" && allTimes.length >= 6) {
                targetMin = allTimes[5];
            } else if (session === "Market Closed") {
                if (allTimes.length === 2) {
                    if (nowMin < allTimes[0]) targetMin = allTimes[0];
                } else if (allTimes.length >= 6) {
                    if (nowMin < allTimes[0]) targetMin = allTimes[0];
                    else if (nowMin >= allTimes[1] && nowMin < allTimes[2]) targetMin = allTimes[2];
                    else if (nowMin >= allTimes[3] && nowMin < allTimes[4]) targetMin = allTimes[4];
                }
            }

            if (targetMin !== null) {
                let remSec = (targetMin - nowMin) * 60 - nowSec;
                if (remSec < 0) remSec += 24 * 3600;
                const h = Math.floor(remSec / 3600);
                const m = Math.floor((remSec % 3600) / 60);
                const s = remSec % 60;
                const parts: string[] = [];
                if (h > 0) parts.push(`${h}h`);
                parts.push(`${String(m).padStart(2, "0")}m`);
                parts.push(`${String(s).padStart(2, "0")}s`);
                setCountdown(parts.join(" "));
            } else {
                setCountdown("");
            }
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [stock, session]);

    if (session === null) return null;

    const hhmm2min = (t: number) => {
        const s = String(t).padStart(4, "0");
        return parseInt(s.slice(0, 2)) * 60 + parseInt(s.slice(2));
    };
    const nowMinExchange = stock.exchange_tz
        ? (() => {
              const d = dayjs().tz(stock.exchange_tz.trimEnd());
              return d.hour() * 60 + d.minute();
          })()
        : 0;

    const sessionColor = session === "Main session" ? ortexColor : session === "Market Closed" ? "#9e9e9e" : "#ffc107";
    const sessionIcon = session === "Main session" ? <WbSunnyOutlinedIcon sx={{ fill: sessionColor }} /> : session === "Market Closed" ? <DoDisturbOnOutlinedIcon sx={{ fill: sessionColor }} /> : <WbTwilightIcon sx={{ fill: sessionColor }} />;

    const times = stock.exchange_th?.split(",").join("-").split("-").map(Number) ?? [];
    const segments =
        times.length === 2
            ? [{ label: "Main session", color: ortexColor, start: times[0], end: times[1] }]
            : times.length >= 6
              ? [
                    { label: "Premarket", color: "#ffc107", start: times[0], end: times[1] },
                    { label: "Main session", color: ortexColor, start: times[2], end: times[3] },
                    { label: "After hours", color: "#ffc107", start: times[4], end: times[5] },
                ]
              : [];

    const formatSessionTime = (t: number) => {
        const s = String(t).padStart(4, "0");
        const hh = parseInt(s.slice(0, 2));
        const mm = parseInt(s.slice(2));
        if (!useLocalTz || !stock.exchange_tz) return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
        return dayjs().tz(stock.exchange_tz.trimEnd()).hour(hh).minute(mm).second(0).tz(userZone).format("HH:mm");
    };

    const descriptions: Record<string, string> = {
        "Main session": "Market is open for regular trading.",
        Premarket: "Pre-market trading is currently active.",
        "After hours": "After-hours trading is currently active.",
        "Market Closed": "The market is currently closed.",
    };

    const tzLabel = useLocalTz ? `${zoneCity(userZone)} ${zoneLabel(userZone)}` : (stock.exchange_tz?.trim().replace(/_/g, " ") ?? "");
    const clockDisplay = useLocalTz ? dayjs().tz(userZone).format("HH:mm") : dayjs().tz(stock.exchange_tz?.trimEnd()).format("HH:mm");

    return (
        <>
            {isCompact ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mx: 1, cursor: "pointer" }} onClick={(e) => setPopperAnchor(popperAnchor ? null : e.currentTarget)}>
                    {sessionIcon}
                </Box>
            ) : (
                <Chip
                    size="small"
                    variant="outlined"
                    label={<Typography variant="captionSessionChip">{session}</Typography>}
                    sx={{ mx: 2, px: 0.5, cursor: "pointer", opacity: 0.8 }}
                    icon={sessionIcon}
                    onClick={(e) => setPopperAnchor(popperAnchor ? null : e.currentTarget)}
                />
            )}
            <Popper open={Boolean(popperAnchor)} anchorEl={popperAnchor} placement="bottom-start" sx={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={() => setPopperAnchor(null)}>
                    <Paper elevation={4} sx={{ p: 2, minWidth: 250 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            {sessionIcon}
                            <Typography variant="subtitle2" sx={{ color: sessionColor, fontWeight: 600 }}>
                                {session}
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: countdown ? 1 : segments.length ? 2 : 0 }}>
                            {descriptions[session]}
                        </Typography>

                        {countdown && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2, bgcolor: "action.hover", borderRadius: 1, px: 1, py: 0.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1 }}>
                                    {session === "Market Closed" ? "Next session in" : "Closes in"}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", fontVariantNumeric: "tabular-nums", ml: 0.5, lineHeight: 1 }}>
                                    {countdown}
                                </Typography>
                            </Box>
                        )}

                        {segments.map((seg) => {
                            const isActive = session === seg.label;
                            const pct = isActive ? Math.min(100, Math.max(0, ((nowMinExchange - hhmm2min(seg.start)) / (hhmm2min(seg.end) - hhmm2min(seg.start))) * 100)) : 0;
                            return (
                                <Box key={seg.label} sx={{ mb: 1 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: isActive ? seg.color : "text.disabled", fontWeight: isActive ? 700 : 400 }}>
                                                {seg.label}
                                            </Typography>
                                            {isActive && <Chip label="Now" size="small" sx={{ height: 14, fontSize: 9, bgcolor: "transparent", color: seg.color, border: `1px solid ${seg.color}`, "& .MuiChip-label": { px: 0.5 } }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            {formatSessionTime(seg.start)} , {formatSessionTime(seg.end)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: "grey.600", opacity: isActive ? 1 : 0.3, overflow: "hidden", position: "relative" }}>
                                        {isActive && <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, bgcolor: seg.color }} />}
                                    </Box>
                                </Box>
                            );
                        })}

                        {stock.exchange_tz && (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: segments.length ? 1 : 0 }}>
                                <Typography variant="captionSessionChip" sx={{ color: "text.disabled" }}>
                                    {tzLabel} · {clockDisplay}
                                </Typography>
                                <Tooltip title={useLocalTz ? "Switch to exchange time" : "Switch to my time zone"}>
                                    <IconButton size="small" onClick={() => setUseLocalTz((v) => !v)} sx={{ p: 0.25 }}>
                                        <PublicIcon sx={{ fontSize: 14, color: useLocalTz ? "primary.main" : "text.disabled" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
};

export default StockHeaderSession;
